import React, { useEffect, useState } from "react";
import classes from "./CardsSection.module.scss";
import CustomImage from "../../CustomImage";
import Chevron from "../../UI/Icons/Chevron";
import { isFrappeOrJotFormClass } from "../../../utils/helpers";
import Dropdown from "../../UI/Dropdown/Dropdown";

interface JotForm {
  id: number;
  jotformscript: string | null;
  jotformid: string | null;
}

interface ScholarshipMeta {
  id: number;
  metaTitle?: string | null;
  metaDescription?: string | null;
  jotforms?: JotForm[];
}

interface StrapiImage {
  id: number;
  name: string;
  alternativeText?: string;
  url: string;
  width?: number;
  height?: number;
}

interface ScholarshipPageData {
  id?: number;
  title?: string;
  meta?: ScholarshipMeta;
}

interface CountryCardsData {
  id?: number;
  title?: string | null;
  description?: string | null;
}

interface ScholarshipsCountry {
  id?: number;
  countryName?: string | null;
}

interface CountryScholarship {
  id: number;
  title: string | null;
  slug?: string | null;
  showcaseCard: any;
  meta?: ScholarshipMeta;
  scholarships_country?: ScholarshipsCountry | null;
}

interface CardItem {
  showcaseCard: any;
  title: string | null;
  scholarshipData: ScholarshipPageData;
  slug?: string | null;
}

const Card = ({ showcaseCard, title, scholarshipData, slug, ...props }: CardItem) => {
  return (
    <div className={classes["card"]}>
      <div className={classes["card-image"]}>
        <CustomImage src={showcaseCard?.cardImg?.url ?? "/images/default-intake-bg.jpg"} alt={title ?? ""} layout="fill" objectFit="cover" />
      </div>
      <div className={classes["card-content"]}>
        <h3 className={classes["card-title"]}>{showcaseCard?.title ?? ""}</h3>
        <div className={classes["card-buttons"]}>
          <a className={classes[`card-button-outline`]} href={`/student-services/study-abroad-scholarships/${slug}`} target="_blank">
            Know More
          </a>
          <button className={`${classes[`card-button-gradient`]} ${isFrappeOrJotFormClass(scholarshipData?.meta?.jotforms?.[0]?.jotformid ?? undefined)}`}>
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
};

interface CardsSectionProps {
  scholarshipData: ScholarshipPageData;
  countryCardsData: CountryCardsData;
  countrySpecificScholarshipsData: CountryScholarship[];
}

const CardsSection = ({ scholarshipData, countryCardsData, countrySpecificScholarshipsData }: CardsSectionProps) => {
  const [visibleCount, setVisibleCount] = useState(21);
  const [selectedCountry, setSelectedCountry] = useState<{ label: string; value: string } | null>(null);
  const [filteredCards, setFilteredCards] = useState<CountryScholarship[]>([]);

  useEffect(() => {
    setVisibleCount(21);
    setFilteredCards(countrySpecificScholarshipsData ?? []);
  }, [scholarshipData]);

  const selectHandler = (option: any) => {
    setFilteredCards((prev) => countrySpecificScholarshipsData?.filter((country) => country?.scholarships_country?.countryName === option.value));
    setSelectedCountry(option);
  };

  return (
    <section className={classes["cards-section-wrapper"]}>
      <div className={classes["cards-section-container"]}>
        <h2 className={classes["section-heading"]}>{countryCardsData?.title ?? ""}</h2>
        <div className={classes["section-description"]} dangerouslySetInnerHTML={{ __html: countryCardsData?.description ?? "" }}></div>
        <div className={classes["dropdown-container"]}>
          <Dropdown
            options={
              countrySpecificScholarshipsData
                ?.filter((country) => !!country?.scholarships_country?.countryName)
                .map((country) => ({
                  label: country.scholarships_country?.countryName ?? "",
                  value: country.scholarships_country?.countryName ?? "",
                })) ?? []
            }
            onChange={selectHandler}
            placeholder="Select Country"
            value={selectedCountry}
            showClear={true}
            onClear={() => {
              setSelectedCountry(null);
              setFilteredCards(countrySpecificScholarshipsData)
            }}
            searchable
          />
        </div>

        <div className={classes["cards-container"]}>
          {filteredCards?.length > 0 &&
            filteredCards
              ?.slice(0, visibleCount)
              .map((card: CountryScholarship, index: number) => <Card key={index} scholarshipData={scholarshipData} {...card} />)}
        </div>
        {filteredCards?.length > visibleCount && (
          <button className={classes["view-more-btn"]} onClick={() => setVisibleCount((count) => count + 21)}>
            View More
            <Chevron color="#FF7361" width={6} height={10} />
          </button>
        )}
      </div>
    </section>
  );
};

export default CardsSection;
